import type { BadgeTone } from '@/components/ui/Badge';
import type { Option } from '@/constants/institute';
import type { SubjectCategory } from '@/types/subject';

/**
 * The Subjects page opened on one class's lists, that class already picked —
 * where class setup's Manage leads, and where a newly created class is offered
 * to go next.
 */
export function subjectsForClassPath(classUuid: string): string {
  const params = new URLSearchParams({
    [SUBJECTS_QUERY.view]: SUBJECTS_BY_CLASS_VIEW,
    [SUBJECTS_QUERY.class]: classUuid,
  });
  return `/admin/subjects?${params.toString()}`;
}

/** The Subjects page's address parameters, read where `subjectsForClassPath` writes them. */
export const SUBJECTS_QUERY = { view: 'view', class: 'class' } as const;
export const SUBJECTS_BY_CLASS_VIEW = 'by-class';

/** The three categories, in the order a class lists them. */
export const SUBJECT_CATEGORY_ORDER: SubjectCategory[] = ['CORE', 'ELECTIVE', 'LANGUAGE'];

/** One word each, for a badge and the category picker. */
export const SUBJECT_CATEGORY_LABEL: Record<SubjectCategory, string> = {
  CORE: 'Core',
  ELECTIVE: 'Elective',
  LANGUAGE: 'Language',
};

/** The tab and count headings — the same three headings the class form uses. */
export const SUBJECT_CATEGORY_PLURAL: Record<SubjectCategory, string> = {
  CORE: 'Core subjects',
  ELECTIVE: 'Electives',
  LANGUAGE: 'Languages',
};

export const SUBJECT_CATEGORY_HINT: Record<SubjectCategory, string> = {
  CORE: 'Taught to every student in the classes that list it.',
  ELECTIVE: 'Chosen by the student, from those a class offers.',
  LANGUAGE: 'A language a class offers — first, second or third.',
};

export const SUBJECT_CATEGORY_TONE: Record<SubjectCategory, BadgeTone> = {
  CORE: 'accent',
  ELECTIVE: 'info',
  LANGUAGE: 'success',
};

export const SUBJECT_CATEGORY_OPTIONS: Option<SubjectCategory>[] = SUBJECT_CATEGORY_ORDER.map((category) => ({
  value: category,
  label: SUBJECT_CATEGORY_LABEL[category],
}));
